/*
 * storefront-inventory.js
 *
 * Corrects inventory display to only count stock from fulfillment-enabled
 * locations by querying the Storefront API's quantityAvailable field.
 *
 * Shopify Liquid's variant.inventory_quantity sums ALL locations, which
 * includes locations with online fulfillment disabled. The Storefront API's
 * quantityAvailable returns the correct sellable quantity.
 */
(function () {
	'use strict'

	var token = window.theme && window.theme.settings && window.theme.settings.storefrontApiToken
	if (!token) return

	var shopUrl = window.theme && window.theme.routes && window.theme.routes.shop_url
	if (!shopUrl) return

	var apiUrl = shopUrl + '/api/2024-10/graphql.json'
	var cache = {}

	function fetchVariant(variantId) {
		if (!variantId) return Promise.resolve(null)

		var key = String(variantId)
		if (cache.hasOwnProperty(key)) return Promise.resolve(cache[key])

		var query =
			'{ node(id: "gid://shopify/ProductVariant/' +
			key +
			'") { ... on ProductVariant { quantityAvailable availableForSale } } }'

		return fetch(apiUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': token,
			},
			body: JSON.stringify({ query: query }),
		})
			.then(function (r) {
				return r.json()
			})
			.then(function (data) {
				var node = data && data.data ? data.data.node : null
				if (!node) return null
				var result = {
					qty: node.quantityAvailable,
					availableForSale: node.availableForSale,
				}
				cache[key] = result
				return result
			})
			.catch(function (err) {
				console.warn('[storefront-inventory] Fetch failed:', err)
				return null
			})
	}

	function getVariantId(productInfoEl) {
		var input = productInfoEl.querySelector('[name="id"]')
		return input ? input.value : null
	}

	function correctInventory(productInfoEl) {
		var variantId = getVariantId(productInfoEl)
		if (!variantId) return

		fetchVariant(variantId).then(function (result) {
			if (!result) return

			// Skip variants that don't report a real tracked quantity (e.g. gift cards,
			// allow-oversell items). quantityAvailable is null for untracked variants and
			// can be negative for oversold ones — in both cases availableForSale is the
			// source of truth, so leave Liquid's determination alone.
			if (result.qty === null || result.qty === undefined || result.qty < 0) return

			// Verify variant hasn't changed during async fetch
			if (getVariantId(productInfoEl) !== variantId) return

			applyCorrection(productInfoEl, result.qty)
		})
	}

	function applyCorrection(container, qty) {
		var quantityInput = container.querySelector('[data-quantity-field]')
		var cartQty = 0

		// Correct quantity selector max
		if (quantityInput) {
			cartQty = parseInt(quantityInput.dataset.cartQuantity) || 0
			var correctedMax = qty - cartQty

			quantityInput.dataset.inventoryQuantity = String(qty)

			// Only adjust max if inventory tracking is active (input has a max set)
			if (quantityInput.hasAttribute('max')) {
				var newMax = Math.max(correctedMax, 0)
				quantityInput.max = String(newMax)
				quantityInput.dataset.max = String(newMax)

				// Clamp current value if it exceeds new max
				var currentVal = parseInt(quantityInput.value) || 1
				if (newMax > 0 && currentVal > newMax) {
					quantityInput.value = String(newMax)
				}
			}

			// Update increase button disabled state
			var increaseBtn = container.querySelector('[data-quantity-plus]')
			if (increaseBtn) {
				var val = parseInt(quantityInput.value) || 1
				var max = parseInt(quantityInput.max)
				increaseBtn.disabled = !isNaN(max) && val >= max
			}
		}

		// Correct inventory countdown display
		var countdownWrapper = container.querySelector('[data-remaining-wrapper]')
		if (countdownWrapper) {
			// Don't override future arrival or unavailable states
			if (
				countdownWrapper.classList.contains('count-is-future') ||
				countdownWrapper.classList.contains('count-is-unavailable')
			) {
				return
			}

			var maxInventory = parseInt(countdownWrapper.dataset.remainingMax) || 10
			var maxInventoryQty = maxInventory + 1
			var variantInventory = qty - cartQty

			var remainingCount = countdownWrapper.querySelector('[data-remaining-count]')

			countdownWrapper.classList.remove('count-is-in', 'count-is-low', 'count-is-out')

			if (variantInventory > 0 && variantInventory <= maxInventory) {
				countdownWrapper.classList.add('count-is-low')
				if (remainingCount) {
					remainingCount.textContent = String(Math.min(variantInventory, maxInventoryQty))
				}
			} else if (variantInventory > 0) {
				countdownWrapper.classList.add('count-is-in')
			} else {
				countdownWrapper.classList.add('count-is-out')
			}
		}

		// Correct buy button max-inventory-reached state
		var productForm = container.querySelector('[data-product-form]')
		if (productForm) {
			var maxReached = qty - cartQty <= 0
			productForm.setAttribute('data-max-inventory-reached', String(maxReached))
		}
	}

	// Correct on initial page load
	function initAll() {
		document.querySelectorAll('product-info').forEach(correctInventory)
	}

	// Correct after variant change (fires after DOM is updated by product-info.js)
	if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
		window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (event) {
			var sectionId = event && event.data && event.data.sectionId
			if (!sectionId) return

			// Find the product-info element for this section
			var productInfoEl =
				document.querySelector('product-info[data-section="' + sectionId + '"]') ||
				document.querySelector('product-info[data-section-id="' + sectionId + '"]')

			if (productInfoEl) {
				correctInventory(productInfoEl)
			}
		})

		// Also correct after cart updates (cart qty affects available qty)
		window.subscribe(window.theme.PUB_SUB_EVENTS.cartUpdate, function () {
			// Clear cache since cart quantities changed
			cache = {}
			document.querySelectorAll('product-info').forEach(correctInventory)
		})
	}

	// Re-correct after quantity input changes, because checkLiveCartInfo()
	// fetches the Liquid section which still has the uncorrected inventory_quantity
	// and may override our corrections on the product form
	document.addEventListener(
		'change',
		function (e) {
			if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-quantity-field')) {
				var productInfo = e.target.closest('product-info')
				if (productInfo) {
					setTimeout(function () {
						var variantId = getVariantId(productInfo)
						if (variantId && cache.hasOwnProperty(String(variantId))) {
							var cached = cache[String(variantId)]
							if (cached && cached.qty !== null && cached.qty !== undefined && cached.qty >= 0) {
								applyCorrection(productInfo, cached.qty)
							}
						}
					}, 500)
				}
			}
		},
		true
	)

	// Correct when new product-info elements load (quick views)
	document.addEventListener('theme:product-info:loaded', function (e) {
		var productInfo = e.target.closest('product-info') || e.target
		if (productInfo && productInfo.tagName === 'PRODUCT-INFO') {
			correctInventory(productInfo)
		}
	})

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAll)
	} else {
		initAll()
	}
})()
